import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";

interface DowngradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: string;
  targetPlan: string;
  renewalDate: string | null;
  isProcessing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Feature definitions with availability by plan
const featureDefinitions = [
  { key: "proBadge", base: false, pro: true, plus: true },
  { key: "priorityRanking", base: false, pro: true, plus: true },
  { key: "multilingualBio", base: false, pro: true, plus: true },
  { key: "coverMedia", base: false, pro: true, plus: true },
  { key: "eventCreation", base: false, pro: true, plus: true },
  { key: "plusBadge", base: false, pro: false, plus: true },
  { key: "companyPage", base: false, pro: false, plus: true },
  { key: "teamMembers", base: false, pro: false, plus: true },
  { key: "companyMultilingual", base: false, pro: false, plus: true },
  { key: "jobPostingCredits", base: false, pro: false, plus: true },
];

// Price map
const planPrices: Record<string, number> = {
  base: 70,
  pro: 95,
  plus: 185,
};

export const DowngradeModal = ({
  open,
  onOpenChange,
  currentPlan,
  targetPlan,
  renewalDate,
  isProcessing,
  onConfirm,
  onCancel,
}: DowngradeModalProps) => {
  const { t, i18n } = useTranslation();

  // Calculate lost features
  const getLostFeatures = () => {
    return featureDefinitions.filter((feature) => {
      const currentHas = feature[currentPlan as keyof typeof feature] as boolean;
      const targetHas = feature[targetPlan as keyof typeof feature] as boolean;
      return currentHas && !targetHas;
    });
  };

  const lostFeatures = getLostFeatures();
  const currentPrice = planPrices[currentPlan] || 0;
  const targetPrice = planPrices[targetPlan] || 0;

  // Format renewal date
  const formatRenewalDate = (dateStr: string | null) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(i18n.language === "it" ? "it-IT" : i18n.language === "es" ? "es-ES" : "en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const formattedDate = formatRenewalDate(renewalDate);
  const targetPlanName = t(`subscriptionPlans.packages.${targetPlan}.name`);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322] w-[90vw] sm:w-auto sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-xl">
            {t("pricing.downgrade.dynamic.confirmTitle", { plan: targetPlanName })}
          </AlertDialogTitle>
          {renewalDate && (
            <AlertDialogDescription className="text-base">
              {t("pricing.downgrade.dynamic.renewalInfo", {
                date: formattedDate,
                newPrice: targetPrice,
                oldPrice: currentPrice,
              })}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {lostFeatures.length > 0 && (
          <div className="py-4">
            <p className="font-heading font-semibold text-dark mb-3">
              {t("pricing.downgrade.dynamic.lostFeaturesTitle")}
            </p>
            <ul className="space-y-2">
              {lostFeatures.map((feature) => (
                <li key={feature.key} className="flex items-center gap-2 text-sm font-body">
                  <X className="w-4 h-4 text-destructive flex-shrink-0" />
                  <span className="text-muted-foreground">
                    {t(`subscriptionPlans.table.features.${feature.key}.label`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <AlertDialogFooter className="gap-3">
          <BrutalButton
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton
            variant="danger"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isProcessing}
          >
            {isProcessing ? t("pricing.plans.loading") : t("pricing.downgrade.dynamic.confirm")}
          </BrutalButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
